import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';
import { useRouters } from '../api/queries';
import type { ImportResult, RouterApiKind, RouterDto, RouterSystemInfo } from '../api/types';
import { useAuth } from '../auth/auth-context';
import { RouterStatusBadge } from '../components/status';
import { Card, ConfirmDialog, Empty, ErrorAlert, Field, Modal, Spinner } from '../components/ui';
import { formatDuration, formatRelative } from '../lib/format';

interface RouterFormState {
  name: string;
  host: string;
  apiKind: RouterApiKind;
  apiPort: string;
  useTls: boolean;
  username: string;
  password: string;
  hotspotServer: string;
  notes: string;
  isActive: boolean;
}

const EMPTY_FORM: RouterFormState = {
  name: '',
  host: '',
  apiKind: 'BINARY',
  apiPort: '',
  useTls: false,
  username: '',
  password: '',
  hotspotServer: '',
  notes: '',
  isActive: true,
};

export function RoutersPage() {
  const { hasRole } = useAuth();
  const routers = useRouters();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<RouterDto | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<RouterDto | null>(null);
  const [pageError, setPageError] = useState<unknown>(null);
  const [testResult, setTestResult] = useState<{ name: string; info: RouterSystemInfo } | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const canManage = hasRole('SUPER_ADMIN');
  const canImport = hasRole('SUPER_ADMIN');

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['routers'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const test = useMutation({
    mutationFn: (router: RouterDto) => api.post<RouterSystemInfo>(`/routers/${router.id}/test`),
    onSuccess: (info, router) => {
      setPageError(null);
      setTestResult({ name: router.name, info });
      invalidate();
    },
    onError: (error) => setPageError(error),
  });

  const remove = useMutation({
    mutationFn: (router: RouterDto) => api.del<void>(`/routers/${router.id}`),
    onSuccess: () => {
      setDeleting(null);
      invalidate();
    },
    onError: (error) => setPageError(error),
  });

  const importFromRouter = useMutation({
    mutationFn: (router: RouterDto) => api.post<ImportResult>(`/routers/${router.id}/import`, {}),
    onSuccess: (result) => {
      setPageError(null);
      setImportResult(result);
      void queryClient.invalidateQueries({ queryKey: ['plans'] });
      void queryClient.invalidateQueries({ queryKey: ['hotspot-users'] });
      void queryClient.invalidateQueries({ queryKey: ['pppoe-clients'] });
      invalidate();
    },
    onError: (error) => setPageError(error),
  });

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1>Routers</h1>
          <p>
            Equipos MikroTik gestionados. El transporte se elige por router: BINARY para
            RouterOS v6 y v7, REST sólo para v7.
          </p>
        </div>
        {canManage && (
          <button type="button" className="primary" onClick={() => setCreating(true)}>
            Añadir router
          </button>
        )}
      </div>

      <ErrorAlert error={pageError} onDismiss={() => setPageError(null)} />

      {importResult && (
        <div className={importResult.warnings.length > 0 ? 'alert warn' : 'alert ok'}>
          <strong>Importación de {importResult.routerName} completada.</strong>
          <div style={{ marginTop: 4 }}>
            Planes: {importResult.plans.created} nuevos, {importResult.plans.updated} actualizados ·
            HotSpot: {importResult.hotspotUsers.created} nuevos, {importResult.hotspotUsers.updated}{' '}
            actualizados · PPPoE: {importResult.pppoeClients.created} nuevos,{' '}
            {importResult.pppoeClients.updated} actualizados
          </div>
          {importResult.warnings.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              {importResult.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          )}
          <button
            type="button"
            className="ghost small"
            style={{ marginTop: 8 }}
            onClick={() => setImportResult(null)}
          >
            Cerrar
          </button>
        </div>
      )}

      {testResult && (
        <div className="alert ok">
          <strong>{testResult.name} responde correctamente.</strong>
          <div style={{ marginTop: 4 }}>
            {testResult.info.identity} · RouterOS {testResult.info.version} ·{' '}
            {testResult.info.boardName} · uptime {formatDuration(testResult.info.uptimeSeconds)} · CPU{' '}
            {testResult.info.cpuLoad}%
          </div>
          <button
            type="button"
            className="ghost small"
            style={{ marginTop: 8 }}
            onClick={() => setTestResult(null)}
          >
            Cerrar
          </button>
        </div>
      )}

      <Card>
        {routers.isLoading ? (
          <div className="card-body">
            <Spinner />
          </div>
        ) : routers.data && routers.data.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Host</th>
                  <th>Transporte</th>
                  <th>Estado</th>
                  <th>RouterOS</th>
                  <th className="num">Uptime</th>
                  <th>Visto</th>
                  <th className="actions" />
                </tr>
              </thead>
              <tbody>
                {routers.data.map((router) => (
                  <tr key={router.id}>
                    <td>
                      <strong>{router.name}</strong>
                      {!router.isActive && (
                        <span className="badge neutral" style={{ marginLeft: 6 }}>
                          inactivo
                        </span>
                      )}
                      {router.identity && router.identity !== router.name && (
                        <div className="faint" style={{ fontSize: 12 }}>
                          identity: {router.identity}
                        </div>
                      )}
                    </td>
                    <td className="mono">
                      {router.host}:{router.apiPort}
                      {router.useTls && (
                        <span className="badge info" style={{ marginLeft: 6 }}>
                          TLS
                        </span>
                      )}
                    </td>
                    <td>{router.apiKind}</td>
                    <td>
                      <RouterStatusBadge status={router.status} />
                      {router.lastError && router.status !== 'ONLINE' && (
                        <div className="faint" style={{ fontSize: 12, maxWidth: 260 }}>
                          {router.lastError}
                        </div>
                      )}
                    </td>
                    <td>
                      {router.version ?? '—'}
                      {router.boardName && (
                        <div className="faint" style={{ fontSize: 12 }}>
                          {router.boardName}
                        </div>
                      )}
                    </td>
                    <td className="num">
                      {router.uptimeSeconds ? formatDuration(router.uptimeSeconds) : '—'}
                    </td>
                    <td className="faint">{formatRelative(router.lastSeenAt)}</td>
                    <td className="actions">
                      <button
                        type="button"
                        className="small"
                        disabled={test.isPending}
                        onClick={() => test.mutate(router)}
                      >
                        Probar
                      </button>
                      {canImport && (
                        <button
                          type="button"
                          className="small"
                          style={{ marginLeft: 6 }}
                          disabled={importFromRouter.isPending}
                          onClick={() => importFromRouter.mutate(router)}
                          title="Trae perfiles y usuarios que ya existen en el router: perfiles como planes, usuarios HotSpot y secretos PPPoE como filas del panel"
                        >
                          {importFromRouter.isPending ? 'Actualizando…' : 'Actualizar datos'}
                        </button>
                      )}
                      {canManage && (
                        <>
                          <button
                            type="button"
                            className="small"
                            style={{ marginLeft: 6 }}
                            onClick={() => setEditing(router)}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            className="small danger"
                            style={{ marginLeft: 6 }}
                            onClick={() => setDeleting(router)}
                          >
                            Eliminar
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>
            No hay routers todavía.
            {canManage && ' Añade el primero para empezar a crear usuarios HotSpot.'}
          </Empty>
        )}
      </Card>

      {(creating || editing) && (
        <RouterFormModal
          router={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            invalidate();
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={`Eliminar ${deleting.name}`}
          danger
          busy={remove.isPending}
          confirmLabel="Eliminar router"
          message={
            <>
              <p>
                Se eliminarán también <strong>todos</strong> sus usuarios HotSpot, lotes de
                vouchers y el histórico de sesiones guardados en MikroManager.
              </p>
              <p className="muted">
                Las credenciales que ya existan en el propio MikroTik <strong>no</strong> se borran:
                seguirán funcionando hasta que las quites desde el router.
              </p>
            </>
          }
          onCancel={() => setDeleting(null)}
          onConfirm={() => remove.mutate(deleting)}
        />
      )}
    </main>
  );
}

function RouterFormModal({
  router,
  onClose,
  onSaved,
}: {
  router: RouterDto | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<RouterFormState>(() =>
    router
      ? {
          name: router.name,
          host: router.host,
          apiKind: router.apiKind,
          apiPort: String(router.apiPort),
          useTls: router.useTls,
          username: router.username,
          password: '',
          hotspotServer: router.hotspotServer ?? '',
          notes: router.notes ?? '',
          isActive: router.isActive,
        }
      : EMPTY_FORM,
  );
  const [error, setError] = useState<unknown>(null);
  const [probe, setProbe] = useState<RouterSystemInfo | null>(null);

  const update = <K extends keyof RouterFormState>(key: K, value: RouterFormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const defaultPort = form.apiKind === 'REST' ? (form.useTls ? 443 : 80) : form.useTls ? 8729 : 8728;

  const testConnection = useMutation({
    mutationFn: () =>
      api.post<{ ok: true } & RouterSystemInfo>('/routers/test-connection', {
        host: form.host,
        apiKind: form.apiKind,
        apiPort: form.apiPort ? Number(form.apiPort) : undefined,
        useTls: form.useTls,
        username: form.username,
        password: form.password,
      }),
    onSuccess: (info) => {
      setError(null);
      setProbe(info);
    },
    onError: (caught) => {
      setProbe(null);
      setError(caught);
    },
  });

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: form.name,
        host: form.host,
        apiKind: form.apiKind,
        apiPort: form.apiPort ? Number(form.apiPort) : undefined,
        useTls: form.useTls,
        username: form.username,
        hotspotServer: form.hotspotServer || undefined,
        notes: form.notes || undefined,
        isActive: form.isActive,
        // Al editar, omitir la contraseña deja la guardada intacta.
        ...(form.password ? { password: form.password } : {}),
      };
      return router
        ? api.patch<RouterDto>(`/routers/${router.id}`, payload)
        : api.post<RouterDto>('/routers', payload);
    },
    onSuccess: onSaved,
    onError: (caught) => setError(caught),
  });

  return (
    <Modal
      title={router ? `Editar ${router.name}` : 'Añadir router'}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={() => testConnection.mutate()}
            disabled={testConnection.isPending || !form.host || !form.username || !form.password}
            title={!form.password ? 'Escribe la contraseña para poder probar la conexión' : undefined}
          >
            {testConnection.isPending ? <Spinner label="Probando…" /> : 'Probar conexión'}
          </button>
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="primary"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            {save.isPending ? <Spinner label="Guardando…" /> : 'Guardar'}
          </button>
        </>
      }
    >
      <ErrorAlert error={error} onDismiss={() => setError(null)} />

      {probe && (
        <div className="alert ok">
          <strong>Conexión correcta.</strong> {probe.identity} · RouterOS {probe.version} ·{' '}
          {probe.boardName}
        </div>
      )}

      <div className="form-row">
        <Field label="Nombre">
          <input value={form.name} onChange={(event) => update('name', event.target.value)} />
        </Field>
        <Field label="Host o IP">
          <input
            value={form.host}
            onChange={(event) => update('host', event.target.value)}
            placeholder="192.168.88.1"
          />
        </Field>
      </div>

      <div className="form-row">
        <Field
          label="Transporte"
          hint={form.apiKind === 'REST' ? 'Requiere RouterOS v7 con www-ssl' : 'RouterOS v6 y v7'}
        >
          <select
            value={form.apiKind}
            onChange={(event) => {
              const apiKind = event.target.value as RouterApiKind;
              // REST sólo existe sobre HTTPS, así que TLS deja de ser opcional.
              setForm((current) => ({
                ...current,
                apiKind,
                useTls: apiKind === 'REST' ? true : current.useTls,
                apiPort: '',
              }));
            }}
          >
            <option value="BINARY">API binaria (8728 / 8729)</option>
            <option value="REST">REST (v7, HTTPS)</option>
          </select>
        </Field>

        <Field label="Puerto" hint={`Vacío = ${defaultPort}`}>
          <input
            type="number"
            value={form.apiPort}
            onChange={(event) => update('apiPort', event.target.value)}
            placeholder={String(defaultPort)}
          />
        </Field>
      </div>

      <label className="inline" style={{ marginBottom: 12 }}>
        <input
          type="checkbox"
          checked={form.useTls}
          disabled={form.apiKind === 'REST'}
          onChange={(event) => update('useTls', event.target.checked)}
        />
        Usar TLS
        {form.apiKind === 'REST' && <span className="faint">(obligatorio en REST)</span>}
      </label>

      <fieldset>
        <legend>Credenciales del router</legend>
        <div className="form-row">
          <Field label="Usuario">
            <input
              value={form.username}
              onChange={(event) => update('username', event.target.value)}
              autoComplete="off"
              placeholder="mikromanager"
            />
          </Field>
          <Field
            label="Contraseña"
            hint={router ? 'Vacío = mantener la actual' : 'Se guarda cifrada con AES-256-GCM'}
          >
            <input
              type="password"
              value={form.password}
              onChange={(event) => update('password', event.target.value)}
              autoComplete="new-password"
            />
          </Field>
        </div>
        <p className="faint" style={{ margin: 0, fontSize: 12 }}>
          Usa un usuario dedicado con policy <code>api,read,write,test</code> y restringido a la IP
          de este servidor. Ver docs/ROUTEROS.md §1.
        </p>
      </fieldset>

      <div className="form-row">
        <Field label="Servidor HotSpot" hint="Vacío = todos (all)">
          <input
            value={form.hotspotServer}
            onChange={(event) => update('hotspotServer', event.target.value)}
            placeholder="hotspot1"
          />
        </Field>
        <Field label="Notas">
          <input value={form.notes} onChange={(event) => update('notes', event.target.value)} />
        </Field>
      </div>

      <label className="inline">
        <input
          type="checkbox"
          checked={form.isActive}
          onChange={(event) => update('isActive', event.target.checked)}
        />
        Activo (los routers inactivos no se sondean ni admiten altas)
      </label>
    </Modal>
  );
}
